import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  Card,
  Button,
  Modal,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Backdrop,
  CircularProgress,
  Alert,
  Snackbar,
  Autocomplete
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import apiRequest from "../../apiRequest";
import { endPoints } from "../../apis";

function NewsManagement() {
  const [news, setNews] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [currentId, setCurrentId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const roles = [
      {name : "driver"},
      {name : "customer"}
    ];

  const [newData, setNewData] = useState({
    name_en: "",
    name_fr: "",
    description_en: "",
    description_fr: "",
    role: '',
    status: "active",
  });

  const initialState = {
    name_en: '',
    name_fr: '',
    description_en: '',
    description_fr: '',
    role: '',
    status: 'active',
  };

  // Fetch category with pagination
  const getNews = useCallback(
    async (page) => {
      setLoading(true);
      try {
        const params = { page, total: totalItems };
        const headers = {
          "x-access-token": accessToken,
        };
        const response = await apiRequest.get(endPoints.GET_NEWS,
          params,
          headers,
        );

        if (response.success) {
          const { items, pagination } = response.data;
          setNews(items);
          setTotalPages(pagination.total_pages);
          setCurrentPage(pagination.current_page);
          setTotalItems(pagination.totalItems);
        }
      } catch (error) {
        console.error("Error fetching category:", error);
        setError("Failed to fetch category. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [totalItems, accessToken]
  );

  // Fetch category when currentPage changes
  useEffect(() => {
    setAccessToken(localStorage.getItem("access_token"));
    getNews(currentPage);
  }, [currentPage, getNews]);

  // Handle modal open/close
  const handleOpenModal = () => {
    setOpenModal(true);
    setNewData(initialState);
  };
  const handleCloseModal = () => {
    setOpenModal(false);
    setNewData(initialState);
    setIsEditing(false);
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewData({ ...newData, [name]: value });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      

        const data = {
            name_en : newData.name_en,
            name_fr : newData.name_fr,
            description_en : newData.description_en,
            description_fr : newData.description_fr,
            user_type : newData.role
        }
      // Determine the endpoint and method based on whether it's an edit or add operation
      const endPoint = isEditing
        ? `${endPoints.UPDATE_NEWS}/${currentId}`
        : `${endPoints.ADD_NEWS}`;
      const requestMethod = isEditing ? apiRequest.put : apiRequest.post;

      // Send the request
      const response = await requestMethod(endPoint, data, {
        headers: {
          'x-access-token': accessToken,
        },
      });

      // Handle the response
      if (response.success) {
        setSuccess(isEditing ? "Category updated successfully." : "Category added successfully.");
        getNews(currentPage); // Refresh the Category list
        handleCloseModal(); // Close the modal
      } else {
        setError(response.message || 'Failed to add/update Category.');
      }
    } catch (error) {
      console.error('Error:', error);
      setError('An error occurred while processing your request.');
    } finally {
      setLoading(false);
    }
  };

  // Handle edit servict type
  const handleEdit = (newsObj) => {
    handleOpenModal();
    setIsEditing(true);
    setNewData({
      name_en: newsObj.name_en || "",
      name_fr: newsObj.name_fr || "",
      description_en: newsObj.description_en || "",
      description_fr: newsObj.description_fr || "",
      role : newsObj.user_type || "",
      status: newsObj.status || "active",
    });

    setCurrentId(newsObj.id);
  };

  // Handle delete Category
  const handleDelete = async (id) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Send the DELETE request to the API
      const response = await apiRequest.delete(
        `${endPoints.DELETE_NEWS}/${id}`,
        {
          headers: {
            'x-access-token': accessToken,
          },
        }
      );

      // Handle the response
      if (response.success) {
        setSuccess("News deleted successfully.");

        // Update the local state to remove the deleted Category
        setNews((prev) => prev.filter((newData) => newData.id !== id));
      } else {
        setError(response.message || "Failed to delete the Category.");
      }
    } catch (error) {
      console.error("Error deleting News:", error);
      setError("An error occurred while deleting the Category.");
    } finally {
      setLoading(false);
    }
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage); // Update the current page
    }
  };

  // const handleFileChange = (e, fieldName) => {
  //   const file = e.target.files[0];
  //   if (file) {
  //     // Update the state with the file and its preview
  //     setNewData((prevState) => ({
  //       ...prevState,
  //       [fieldName]: file, // Store the file object
  //       [`${fieldName}_preview`]: URL.createObjectURL(file), // Generate a preview URL
  //     }));
  //   } 
  // };

  const handleStatusToggle = async (id, currentStatus) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const newStatus = currentStatus === "active" ? "inactive" : "active";
      const payload = {
        status: newStatus,
      };

      const headers = {
        'Content-Type': 'application/json',
      };

      // Send the PUT request to update the Category status
      const response = await apiRequest.put(
        `${endPoints.UPDATE_NEWS}/${id}`,
        payload,
        { headers }
      );

      // Handle the response
      if (response.success) {
        setSuccess("News status updated successfully.");

        // Update the local state immediately
        setNews((prev) =>
          prev.map((newData) =>
            newData.id === id ? { ...newData, status: newStatus } : newData
          )
        );
      } else {
        setError(response.message || 'Failed to update service type status.');
      }
    } catch (error) {
      console.error('Error updating service type status:', error);
      setError('An error occurred while updating the service type status.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  News
                </MDTypography>
              </MDBox>
              <MDBox pt={1} px={1}>
                <MDBox
                  pt={1}
                  px={1}
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                  gap={2}
                >
                  <TextField
                    label="Search"
                    variant="outlined"
                    margin="normal"
                    placeholder="Search by title or description"
                    style={{ width: "300px" }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleOpenModal}
                    style={{
                      whiteSpace: "nowrap",
                      color: "white",
                    }}
                  >
                    Add News
                  </Button>
                </MDBox>
                <TableContainer component={Paper} style={{ marginTop: "1px" }}>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell style={{ fontWeight: "bold" }}>Name</TableCell>
                        <TableCell style={{ fontWeight: "bold" }}>Description</TableCell>
                        <TableCell style={{ fontWeight: "bold" }}>Role</TableCell>
                        <TableCell style={{ fontWeight: "bold" }}>Status</TableCell>
                        <TableCell style={{ fontWeight: "bold" }}>Actions</TableCell>
                      </TableRow>
                      {news.map((newsObj) => (
                        <TableRow key={newsObj.id}>
                          <TableCell>{newsObj.name_en + ' / ' + newsObj.name_fr}</TableCell>
                          <TableCell>{newsObj.description_en + ' / ' + newsObj.description_fr}</TableCell>
                          <TableCell>{newsObj.user_type}</TableCell>
                          <TableCell>
                            <Switch
                              checked={newsObj.status === "active"} // Assuming "active" is the active status
                              onChange={() => handleStatusToggle(newsObj.id, newsObj.status)}
                              color="primary"
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              onClick={() => handleEdit(newsObj)}
                              color="primary"
                              size="small"
                            >
                              Edit
                            </Button>
                            <Button
                              onClick={() => {
                                setCurrentId(newsObj.id); // Set the service type ID to delete
                                setDeleteDialogOpen(true); // Open the confirmation dialog
                              }}
                              color="error"
                              size="small"
                              style={{ marginLeft: "8px" }}
                            >
                              Delete
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination Controls */}
                <MDBox mt={2} display="flex" justifyContent="center" alignItems="center">
                  <Button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <MDTypography mx={2}>
                    Page {currentPage} of {totalPages} {/* Display current page and total pages */}
                  </MDTypography>
                  <Button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      {/* Modal for Adding/Editing Service Type */}
      <Modal open={openModal} onClose={handleCloseModal}>
        <MDBox
          p={3}
          bgcolor="white"
          borderRadius="lg"
          style={{
            width: 900,
            margin: "50px auto",
            outline: "none",
            backgroundColor: "white",
            maxHeight: "80vh", // Limit the height of the modal
            overflowY: "auto", // Enable vertical scrolling
          }}
        >
          <MDTypography variant="h6" mb={2}>
            {isEditing ? "Edit News" : "Add News"}
          </MDTypography>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>

            <Grid item xs={12} sm={12}>
                <Autocomplete
                    options={roles}
                    getOptionLabel={(option) => option?.name || ""} 
                    value={newData.role ? roles.find(role => role.name === newData.role) : null}
                    onChange={(event, newValue) => {
                        setNewData({ ...newData, role: newValue ? newValue.name : null });
                    }}
                    renderInput={(params) => <TextField {...params} label="Select Role" />}
                    required
                />
            </Grid>
              {/* Title (EN) */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Title (EN)"
                  name="name_en"
                  value={newData.name_en}
                  onChange={handleInputChange}
                  margin="normal"
                  required
                />
              </Grid>

              {/* Title (FR) */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Title (FR)"
                  name="name_fr"
                  value={newData.name_fr}
                  onChange={handleInputChange}
                  margin="normal"
                  required
                />
              </Grid>

              {/* Description (EN) */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Description (EN)"
                  name="description_en"
                  value={newData.description_en}
                  onChange={handleInputChange}
                  margin="normal"
                  multiline
                  rows={3}
                />
              </Grid>

              {/* Description (FR) */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Description (FR)"
                  name="description_fr"
                  value={newData.description_fr}
                  onChange={handleInputChange}
                  margin="normal"
                  multiline
                  rows={3}
                />
              </Grid>

            </Grid>

            <MDBox mt={3} display="flex" justifyContent="flex-end">
              <Button
                type="submit"
                variant="contained"
                color="primary"
                style={{
                  whiteSpace: "nowrap",
                  color: "white",
                }}
              >
                {isEditing ? "Update" : "Add"}
              </Button>
            </MDBox>
          </form>
        </MDBox>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete News</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this news? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false); // Close the dialog
              handleDelete(currentId); // Proceed with deletion
            }}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Error/Success Snackbar */}
      <Snackbar
        open={!!error || !!success}
        autoHideDuration={3000}
        onClose={() => {
          setError(null);
          setSuccess(null);
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }} // Positioning the Snackbar
      >
        <Alert
          onClose={() => {
            setError(null);
            setSuccess(null);
          }}
          severity={error ? "error" : "success"}
          sx={{ width: '100%' }}
        >
          {error || success}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default NewsManagement;